import React from 'react'
import classNames from 'classnames'
import { isEmpty } from 'lodash'
import { IconChevronRight, IconChevronDown } from '@tabler/icons-react'

import { Button } from '@/components/buttons/Button'
import { Checkbox } from '@/components/forms/Checkbox'
import { Loader } from '@/components/loaders/Loader'

// Must match the @for range in TreeTable.scss; deeper rows share the last indent
const MAX_INDENT_LEVEL = 12

export const TreeTableRow = ({
  row,
  columns,
  rowKey,
  depth,
  showToggle,
  selectable,
  checkboxLabel,
  onRowToggle,
  isRowExpanded,
  onExpandToggle,
}) => {
  const { childRows = [], childRowsLoading, onShowMore, hideCheckbox, checkboxDisabled } = row
  const rowId = row[rowKey]
  const hasChildren = !isEmpty(childRows) || Boolean(row.hasChildren)
  const expanded = hasChildren && isRowExpanded(rowId)

  const renderToggle = () => {
    if (!hasChildren) {
      return <span className="tree-expand-spacer" />
    }

    return (
      <Button
        basic
        small
        square
        data-testid={`expand-${rowId}`}
        aria-label={expanded ? 'Collapse row' : 'Expand row'}
        aria-expanded={expanded}
        onClick={(event) => {
          event.stopPropagation()
          onExpandToggle(row)
        }}
      >
        {expanded ? <IconChevronDown /> : <IconChevronRight />}
      </Button>
    )
  }

  const renderCell = (column, columnIndex) => {
    const cell = row[column.accessor]
    const cellProps = cell?.as ? cell.cellProps : undefined
    const content = cell?.as ? cell.as : cell
    const isFirstColumn = columnIndex === 0

    return (
      <td
        key={column.accessor}
        {...cellProps}
        className={classNames(cellProps?.className, {
          'right-aligned': column.headerAlignment === 'right',
          [`indent-${Math.min(depth, MAX_INDENT_LEVEL)}`]: isFirstColumn && depth > 0,
        })}
      >
        {isFirstColumn ? (
          <div className="flex-align-center medium-gap">
            {showToggle && renderToggle()}
            {selectable && !hideCheckbox && (
              <Checkbox
                data-testid={`row-select-${rowId}`}
                aria-label={checkboxLabel(row)}
                checked={!!row.selected}
                indeterminate={!!row.indeterminate}
                disabled={Boolean(checkboxDisabled)}
                onChange={() => onRowToggle(row)}
              />
            )}
            {content}
          </div>
        ) : (
          content
        )}
      </td>
    )
  }

  // One cell spanning the row, indented like the children it follows
  const renderFooterRow = (content, trProps) => (
    <tr {...trProps}>
      <td colSpan={columns.length} className={`indent-${Math.min(depth + 1, MAX_INDENT_LEVEL)}`}>
        <div className="flex-align-center medium-gap">
          {showToggle && <span className="tree-expand-spacer" />}
          {content}
        </div>
      </td>
    </tr>
  )

  const renderFooter = () => {
    if (childRowsLoading) {
      return renderFooterRow(<Loader size="small" label="Loading…" />, {
        'data-testid': `loading-${rowId}`,
      })
    }

    if (onShowMore) {
      return renderFooterRow(
        <Button basic small data-testid={`show-more-${rowId}`} onClick={onShowMore}>
          Show more
        </Button>
      )
    }

    return null
  }

  return (
    <>
      <tr>{columns.map(renderCell)}</tr>
      {expanded &&
        childRows.map((childRow) => (
          <TreeTableRow
            key={childRow[rowKey]}
            row={childRow}
            columns={columns}
            rowKey={rowKey}
            depth={depth + 1}
            showToggle={showToggle}
            selectable={selectable}
            checkboxLabel={checkboxLabel}
            onRowToggle={onRowToggle}
            isRowExpanded={isRowExpanded}
            onExpandToggle={onExpandToggle}
          />
        ))}
      {expanded && renderFooter()}
    </>
  )
}
